"use client";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { TimelineContent } from "@/components/ui/timeline-animation";
import {VerticalCutReveal} from "@/components/ui/vertical-cut-reveal";
import { cn } from "@/lib/utils";
import NumberFlow from "@number-flow/react";
import { Users, Shield, Package, CheckCheck, AlertTriangle, Building2, FileWarning, X } from "lucide-react";
import { motion } from "motion/react";
import { useRef, useState } from "react";
import { DemoRequestDialog } from "@/components/pricing/DemoRequestDialog";

// All features with availability per plan
const allFeatures = [
  // Core Features
  { name: "Personnel Management", starter: true, professional: true, enterprise: true },
  { name: "Basic Scheduling", starter: true, professional: true, enterprise: true },
  { name: "Team Overview", starter: true, professional: true, enterprise: true },
  { name: "Mobile App Access (iOS & Android)", starter: true, professional: true, enterprise: true },
  { name: "Work Instructions & Missions", starter: true, professional: true, enterprise: true },
  { name: "Basic Reports & Submissions", starter: true, professional: true, enterprise: true },
  { name: "Email Support", starter: true, professional: true, enterprise: true },
  { name: "User Credentials Management", starter: true, professional: true, enterprise: true },
  { name: "Profile Management", starter: true, professional: true, enterprise: true },
  { name: "Settings & Preferences", starter: true, professional: true, enterprise: true },
  
  // Professional Features
  { name: "Advanced Scheduling & Schedule Logs", starter: false, professional: true, enterprise: true },
  { name: "Schedule Alerts & Notifications", starter: false, professional: true, enterprise: true },
  { name: "Performance Evaluation & Tracking", starter: false, professional: true, enterprise: true },
  { name: "Behavior Tracking System", starter: false, professional: true, enterprise: true },
  { name: "Leave Management & Applications", starter: false, professional: true, enterprise: true },
  { name: "Client Management", starter: false, professional: true, enterprise: true },
  { name: "Provider Evaluations", starter: false, professional: true, enterprise: true },
  { name: "Equipment Management", starter: false, professional: true, enterprise: true },
  { name: "Incident Alerts System", starter: false, professional: true, enterprise: true },
  { name: "Enhanced Dashboard & Analytics", starter: false, professional: true, enterprise: true },
  { name: "Priority Support", starter: false, professional: true, enterprise: true },
  { name: "Admin Team Management", starter: false, professional: true, enterprise: true },
  { name: "Guard Team Scheduling", starter: false, professional: true, enterprise: true },
  
  // Enterprise Features
  { name: "HR Management & Recruitment", starter: false, professional: false, enterprise: true },
  { name: "Candidate Testing & Workflows", starter: false, professional: false, enterprise: true },
  { name: "Pending Interviews Management", starter: false, professional: false, enterprise: true },
  { name: "Demographics & Analytics", starter: false, professional: false, enterprise: true },
  { name: "Training Management System", starter: false, professional: false, enterprise: true },
  { name: "Training Modules & Certificates", starter: false, professional: false, enterprise: true },
  { name: "Scheduled Training Sessions", starter: false, professional: false, enterprise: true },
  { name: "Training Analytics & Progress", starter: false, professional: false, enterprise: true },
  { name: "Content Generation (AI-powered)", starter: false, professional: false, enterprise: true },
  { name: "Ethics & Compliance Module", starter: false, professional: false, enterprise: true },
  { name: "Sentinel (Patrol Monitoring)", starter: false, professional: false, enterprise: true },
  { name: "Facility Presence Management", starter: false, professional: false, enterprise: true },
  { name: "Finance & Hourly Costs", starter: false, professional: false, enterprise: true },
  { name: "Supplier Evaluations", starter: false, professional: false, enterprise: true },
  { name: "Complete Documentation Access", starter: false, professional: false, enterprise: true },
  { name: "White-label Solution", starter: false, professional: false, enterprise: true },
  { name: "Dedicated Account Manager", starter: false, professional: false, enterprise: true },
  { name: "Custom Integrations & API", starter: false, professional: false, enterprise: true },
  { name: "24/7 Phone Support", starter: false, professional: false, enterprise: true },
  { name: "SLA Guarantee", starter: false, professional: false, enterprise: true },
  { name: "Custom Training & Onboarding", starter: false, professional: false, enterprise: true },
];

const plans = [
  {
    name: "Basic",
    planKey: "starter" as const,
    description:
      "Kick-start with the essentials for small teams",
    basePrice: 50,
    baseYearlyPrice: 480,  // €50 × 12 × 0.8 = €480 (20% discount)
    pricePerUser: 6,
    yearlyPricePerUser: 57.6,  // €6 × 12 × 0.8 = €57.6 (20% discount)
    includedUsers: 10,
    buttonText: "Request Free Demo",
    buttonVariant: "outline" as const,
    features: [
      { text: "€50 for first 10 users", icon: <Users size={20} /> },
      { text: "€6 per additional user", icon: <Users size={20} /> },
      { text: "Core modules", icon: <Package size={20} /> },
    ],
  },
  {
    name: "Advanced",
    planKey: "professional" as const,
    description:
      "Work faster and save time with advanced features",
    basePrice: 99,
    baseYearlyPrice: 950.4,  // €99 × 12 × 0.8 = €950.4 (20% discount)
    pricePerUser: 5,
    yearlyPricePerUser: 48,  // €5 × 12 × 0.8 = €48 (20% discount)
    includedUsers: 20,
    buttonText: "Request Free Demo",
    buttonVariant: "default" as const,
    popular: true,
    features: [
      { text: "€99 for first 20 users", icon: <Users size={20} /> },
      { text: "€5 per additional user", icon: <Users size={20} /> },
      { text: "Advanced modules", icon: <Package size={20} /> },
    ],
  },
  {
    name: "Enterprise",
    planKey: "enterprise" as const,
    description:
      "A pricing plan customized for your business",
    basePrice: 250,
    baseYearlyPrice: 2400,  // €250 × 12 × 0.8 = €2400 (20% discount)
    pricePerUser: 5,
    yearlyPricePerUser: 48,  // €5 × 12 × 0.8 = €48 (20% discount)
    includedUsers: 50,
    buttonText: "Request Free Demo",
    buttonVariant: "outline" as const,
    features: [
      { text: "€250 for first 50 users", icon: <Users size={20} /> },
      { text: "€5 per additional user", icon: <Users size={20} /> },
      { text: "All modules included", icon: <Package size={20} /> },
    ],
  },
];

const moduleAddons = [
  {
    name: "Whistleblow Module",
    description: "Secure anonymous reporting system for workplace concerns and compliance",
    icon: <AlertTriangle size={24} />,
    pricePerEmployee: 0.5,
    yearlyPricePerEmployee: 4.8,  // €0.5 × 12 × 0.8 = €4.8 (20% discount)
    features: [
      "Anonymous reporting channel",
      "Secure case management",
      "Compliance tracking",
      "Multi-language support",
      "Automated notifications",
      "Audit trail & reporting",
    ],
  },
  {
    name: "Facility Management",
    description: "Complete facility and asset management with maintenance tracking",
    icon: <Building2 size={24} />,
    pricePerEmployee: 2,
    yearlyPricePerEmployee: 20,
    features: [
      "Asset tracking & inventory",
      "Maintenance scheduling",
      "Work order management",
      "Space allocation",
      "Vendor management",
      "Real-time facility monitoring",
    ],
  },
  {
    name: "Risk Assessment Module",
    description: "Comprehensive risk identification and mitigation management system",
    icon: <FileWarning size={24} />,
    flatPrice: 100,
    yearlyFlatPrice: 1000,
    features: [
      "Risk identification & analysis",
      "Mitigation planning",
      "Compliance monitoring",
      "Incident tracking",
      "Automated risk scoring",
      "Detailed reporting & analytics",
    ],
  },
];

const PricingSwitch = ({
  onSwitch,
  className,
}: {
  onSwitch: (value: string) => void;
  className?: string;
}) => {
  const [selected, setSelected] = useState("0");

  const handleSwitch = (value: string) => {
    setSelected(value);
    onSwitch(value);
  };

  return (
    <div className={cn("flex justify-center", className)}>
      <div className="relative z-10 mx-auto flex w-fit rounded-xl bg-neutral-50 border border-gray-200 p-1">
        <button
          onClick={() => handleSwitch("0")}
          className={cn(
            "relative z-10 w-fit cursor-pointer h-12 rounded-xl sm:px-6 px-3 sm:py-2 py-1 font-medium transition-colors sm:text-base text-sm",
            selected === "0"
              ? "text-white"
              : "text-muted-foreground hover:text-black",
          )}
        >
          {selected === "0" && (
            <motion.span
              layoutId={"switch"}
              className="absolute top-0 left-0  h-12 w-full rounded-xl border-4 shadow-sm shadow-blue-600 border-blue-600 bg-gradient-to-t from-blue-500 via-blue-400 to-blue-600"
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            />
          )}
          <span className="relative">Monthly Billing</span>
        </button>

        <button
          onClick={() => handleSwitch("1")}
          className={cn(
            "relative z-10 w-fit cursor-pointer h-12 flex-shrink-0 rounded-xl sm:px-6 px-3 sm:py-2 py-1 font-medium transition-colors sm:text-base text-sm",
            selected === "1"
              ? "text-white"
              : "text-muted-foreground hover:text-black",
          )}
        >
          {selected === "1" && (
            <motion.span
              layoutId={"switch"}
              className="absolute top-0 left-0  h-12 w-full rounded-xl border-4 shadow-sm shadow-blue-600 border-blue-600 bg-gradient-to-t from-blue-500 via-blue-400 to-blue-600"
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            />
          )}
          <span className="relative flex items-center gap-2">
            Yearly Billing
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-900">
              Save 20%
            </span>
          </span>
        </button>
      </div>
    </div>
  );
};

export default function Pricing() {
  const pricingRef = useRef<HTMLDivElement>(null);
  const [isYearly, setIsYearly] = useState(false);
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string>("");

  const revealVariants = {
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: {
        delay: i * 0.4,
        duration: 0.5,
      },
    }),
    hidden: {
      filter: "blur(10px)",
      y: -20,
      opacity: 0,
    },
  };

  const togglePricingPeriod = (value: string) =>
    setIsYearly(Number.parseInt(value) === 1);

  return (
    <div
      className="px-4 pt-20 min-h-screen max-w-7xl mx-auto relative"
      ref={pricingRef}
    >
      <article className="text-left mb-6 space-y-4 max-w-2xl">
        <h2 className="md:text-6xl text-4xl capitalize font-medium text-gray-900 mb-4">
          <VerticalCutReveal
            splitBy="words"
            staggerDuration={0.15}
            staggerFrom="first"
            reverse={true}
            containerClassName="justify-start"
            transition={{
              type: "spring",
              stiffness: 250,
              damping: 40,
              delay: 0,
            }}
          >
            Choose the perfect plan for your team
          </VerticalCutReveal>
        </h2>

        <TimelineContent
          as="p"
          animationNum={0}
          timelineRef={pricingRef}
          customVariants={revealVariants}
          className="md:text-base text-sm text-gray-600 w-[80%]"
        >
          Flexible pricing based on users, admins, and modules. Scale as you grow with our comprehensive shift management platform.
        </TimelineContent>

        <TimelineContent
          as="div"
          animationNum={1}
          timelineRef={pricingRef}
          customVariants={revealVariants}
        >
          <PricingSwitch onSwitch={togglePricingPeriod} className="w-fit" />
        </TimelineContent>
      </article>

      <div className="grid md:grid-cols-3 gap-4 py-6">
        {plans.map((plan, index) => (
          <TimelineContent
            key={plan.name}
            as="div"
            animationNum={2 + index}
            timelineRef={pricingRef}
            customVariants={revealVariants}
          >
            <Card
              className={`relative border border-neutral-200 ${
                plan.popular
                  ? "ring-2 ring-blue-500 bg-blue-50"
                  : "bg-white "
              }`}
            >
              <CardHeader className="text-left ">
                <div className="flex justify-between">
                  <h3 className="xl:text-3xl md:text-2xl text-3xl font-semibold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  {plan.popular && (
                    <div className="">
                      <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        Popular
                      </span>
                    </div>
                  )}
                </div>
                <p className="xl:text-sm md:text-xs text-sm text-gray-600 mb-4">
                  {plan.description}
                </p>
                <div className="space-y-2">
                  {plan.includedUsers > 0 ? (
                    <>
                      <div className="flex items-baseline">
                        <span className="text-4xl font-semibold text-gray-900">
                          €
                          <NumberFlow
                            value={isYearly ? plan.baseYearlyPrice : plan.basePrice}
                            className="text-4xl font-semibold"
                          />
                        </span>
                        <span className="text-gray-600 ml-1 text-sm">
                          /{isYearly ? "year" : "month"}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        For the first {plan.includedUsers} users
                      </p>
                      <p className="text-sm text-gray-600">
                        €{isYearly ? plan.yearlyPricePerUser : plan.pricePerUser} / {isYearly ? "year" : "month"} for each additional user
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline">
                        <span className="text-4xl font-semibold text-gray-900">
                          €
                          <NumberFlow
                            value={isYearly ? plan.baseYearlyPrice : plan.basePrice}
                            className="text-4xl font-semibold"
                          />
                        </span>
                        <span className="text-gray-600 ml-1 text-sm">
                          /{isYearly ? "year" : "month"}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        + €{isYearly ? plan.yearlyPricePerUser : plan.pricePerUser} / {isYearly ? "year" : "month"} per user
                      </p>
                    </>
                  )}
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                <button
                  onClick={() => {
                    setSelectedPlan(plan.name);
                    setDemoDialogOpen(true);
                  }}
                  className={`w-full mb-6 p-4 text-xl rounded-xl transition-all hover:scale-105 ${
                    plan.popular
                      ? "bg-gradient-to-t from-blue-600 to-blue-700  shadow-lg shadow-blue-500 border border-blue-500 text-white"
                      : plan.buttonVariant === "outline"
                        ? "bg-gradient-to-t from-neutral-900 to-neutral-600  shadow-lg shadow-neutral-900 border border-neutral-700 text-white"
                        : ""
                  }`}
                >
                  {plan.buttonText}
                </button>

                <div className="space-y-3 mb-6 pb-4 border-b border-neutral-200">
                  <h2 className="text-xl font-semibold uppercase text-gray-900 mb-3">
                    Plan Limits
                  </h2>
                  <ul className="space-y-2 font-semibold">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-3">
                        <span className="h-10 w-10 bg-blue-50 border border-blue-200 rounded-lg grid place-content-center text-blue-600">
                          {feature.icon}
                        </span>
                        <span className="text-sm text-gray-900 font-medium">{feature.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="space-y-3 pt-4">
                  <h4 className="font-medium text-base text-gray-900 mb-3 uppercase tracking-wide">
                    All Features
                  </h4>
                  <ul className="space-y-2">
                    {allFeatures.map((feature, featureIndex) => {
                      const isIncluded = feature[plan.planKey];
                      return (
                        <li key={featureIndex} className="flex items-start gap-2">
                          {isIncluded ? (
                            <span className="h-5 w-5 bg-white border border-blue-500 rounded-full grid place-content-center mt-0.5 flex-shrink-0">
                              <CheckCheck className="h-3 w-3 text-blue-500" />
                            </span>
                          ) : (
                            <span className="h-5 w-5 bg-gray-100 border border-gray-300 rounded-full grid place-content-center mt-0.5 flex-shrink-0">
                              <X className="h-3 w-3 text-gray-400" />
                            </span>
                          )}
                          <span className={`text-sm ${isIncluded ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                            {feature.name}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TimelineContent>
        ))}
      </div>

      {/* Modular Add-ons Section */}
      <div className="container mx-auto px-4 py-16">
        <TimelineContent
          as="div"
          animationNum={5}
          timelineRef={pricingRef}
          customVariants={revealVariants}
          className="text-center mb-12"
        >
          <h2 className="md:text-5xl text-3xl font-medium text-gray-900 mb-4">
            Optional Add-on Modules
          </h2>
          <p className="md:text-base text-sm text-gray-600 max-w-2xl mx-auto">
            Enhance your plan with specialized modules tailored to your business needs
          </p>
        </TimelineContent>

        <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {moduleAddons.map((addon, index) => (
            <TimelineContent
              key={addon.name}
              as="div"
              animationNum={6 + index}
              timelineRef={pricingRef}
              customVariants={revealVariants}
            >
              <Card className="relative border-2 border-blue-200 bg-gradient-to-br from-white to-blue-50/30 hover:border-blue-400 transition-all duration-300 hover:shadow-xl h-full">
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto mb-4 h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shadow-lg">
                    {addon.icon}
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                    {addon.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {addon.description}
                  </p>
                  <div className="flex flex-col items-center justify-center">
                    {addon.pricePerEmployee ? (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-gray-900">
                            €
                            <NumberFlow
                              value={isYearly ? addon.yearlyPricePerEmployee : addon.pricePerEmployee}
                              className="text-3xl font-bold"
                            />
                          </span>
                          <span className="text-gray-600 text-sm">
                            per employee/{isYearly ? "year" : "month"}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-gray-900">
                            €
                            <NumberFlow
                              value={isYearly ? addon.yearlyFlatPrice : addon.flatPrice}
                              className="text-3xl font-bold"
                            />
                          </span>
                          <span className="text-gray-600 text-sm">
                            /{isYearly ? "year" : "month"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  <button className="w-full mb-6 p-3 text-lg rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg transition-all duration-300">
                    Add to Plan
                  </button>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-gray-900 mb-3 uppercase tracking-wide">
                      Module Features
                    </h4>
                    <ul className="space-y-2">
                      {addon.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-2">
                          <span className="h-5 w-5 bg-blue-100 border border-blue-300 rounded-full grid place-content-center mt-0.5 flex-shrink-0">
                            <CheckCheck className="h-3 w-3 text-blue-600" />
                          </span>
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TimelineContent>
          ))}
        </div>
      </div>

      {/* Demo Request Dialog */}
      <DemoRequestDialog
        open={demoDialogOpen}
        onOpenChange={setDemoDialogOpen}
        planName={selectedPlan}
      />
    </div>
  );
}
